import { useEffect, useRef, useState } from 'react'

import { GetScreenState as BLGetScreenState } from './../../utilities/BL/Admin/Overlays/Noisemeter'

import { motion } from 'framer-motion'
import Confetti from 'react-confetti'


const Noisemeter = ({ data, isActiveWomensMode }) => { 
    
    // --- ESTADOS ---
    const [gameState, setGameState] = useState('WAITING'); 
    const [zones, setZones] = useState([]);
    const [winner, setWinner] = useState(null);

    const pollingRef = useRef(null);
    const { innerWidth: width, innerHeight: height } = window;

    // --------------------------------------------------------------------------------
    // LÓGICA DE DATOS
    // --------------------------------------------------------------------------------
    useEffect(() => {
        
        const updateScreen = (incoming) => {
            if (!incoming) return;

            // 1. NORMALIZACIÓN DE DATOS
            const rawStatus = incoming.status !== undefined ? incoming.status : incoming.Status;
            const rawWinner = incoming.winnerZone || incoming.WinnerZone;
            
            // Buscamos Zones (con datos) Y activeZonesList (solo nombres)
            const rawZones = incoming.zones || incoming.Zones;
            const rawActiveList = incoming.activeZonesList || incoming.ActiveZonesList;
            
            const hasDates = incoming.startAt || incoming.StartAt;

            // 2. ACTUALIZACIÓN DE BARRAS (Lógica Mejorada)
            
            // PRIORIDAD 1: Si vienen zonas con datos (del Polling o Finished), úsalas.
            if (rawZones && Array.isArray(rawZones) && rawZones.length > 0) {
                const cleanZones = rawZones.map(z => ({
                    zone: z.zone || z.Zone,
                    progressPercent: z.progressPercent !== undefined ? z.progressPercent : z.ProgressPercent
                }));
                setZones(cleanZones);
            } 
            // PRIORIDAD 2: Si NO hay datos, pero sí hay lista de nombres (del Prepare), inicializa en 0.
            else if (rawActiveList && Array.isArray(rawActiveList) && rawActiveList.length > 0) {
                // Solo inicializamos si no tenemos zonas ya cargadas, para evitar parpadeos
                // O si queremos forzar el reinicio visual.
                const initialZones = rawActiveList.map(name => ({
                    zone: name,
                    progressPercent: 0
                }));
                setZones(initialZones);
            }

            // 3. DETERMINAR ESTADO

            // --- CASO A: YA HAY GANADOR ---
            if (rawWinner && rawWinner !== 'none' && rawWinner !== 'tie' && rawWinner !== '') {
                setWinner(rawWinner);
                setGameState('FINISHED');
                return; 
            }
            if (rawWinner === 'tie') {
                setWinner('tie');
                setGameState('FINISHED');
                return;
            }
            if (rawStatus === 2 || rawStatus === 'Finished') {
                setGameState('FINISHED');
                return;
            }

            // --- CASO B: JUEGO CORRIENDO ---
            // Criterio 1: Status explícito
            // Criterio 2: Prepare implícito (sin status pero con fechas)
            const isRunningExplicit = rawStatus === 1 || rawStatus === 'Running';
            const isImplicitStart = rawStatus === undefined && hasDates; 

            if (isRunningExplicit || isImplicitStart) {
                setGameState('RUNNING');
                setWinner(null); 
                return;
            }

            // --- CASO C: ESPERA ---
            setGameState('WAITING');
            setWinner(null);
        };

        // A. Procesar datos por props (WebSocket / Padre)
        if (data) updateScreen(data);

        // B. Polling (Consultar API)
        const fetchState = async () => {
            if (typeof BLGetScreenState !== 'undefined') {
                try {
                    const res = await BLGetScreenState();
                    if (res?.success && res?.data) {
                        updateScreen(res.data);
                    }
                } catch (e) {
                    console.error("Error fetching screen state", e);
                }
            }
        };

        if (!pollingRef.current) {
            pollingRef.current = setInterval(fetchState, 500);
        }

        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };

    }, [data]); 

    // --------------------------------------------------------------------------------
    // RENDERIZADO VISUAL
    // --------------------------------------------------------------------------------
    
    // =========================================================================
    // AJUSTE: Mapeo de estilos exclusivo para Juanjolote Local y Visita
    // =========================================================================
    const getZoneStyle = (zoneName) => {
        const n = (zoneName || '').toLowerCase().trim();
        
        // Zona 1 = Juanjolote Local (Usamos el rojo de la paleta previa)
        if (n === 'zona1') return { colorClass: 'red', hex: '#db166c' };
        
        // Zona 2 = Juanjolote Visita (Usamos el azul de la paleta previa)
        if (n === 'zona2') return { colorClass: 'blue', hex: '#1e88e5' };
        
        // Fallback por defecto (rojo estándar)
        return { colorClass: 'red', hex: '#dc3545' };
    };

    // =========================================================================
    // AJUSTE: Función para mostrar el nombre correcto del ganador (Máscara)
    // Transforma 'zona1' en 'Juanjolote Local' para el pop-up de victoria.
    // =========================================================================
    const getVisualZoneName = (zoneName) => {
        const n = (zoneName || '').toLowerCase().trim();
        if (n === 'zona1') return 'Juanjolote Local';
        if (n === 'zona2') return 'Juanjolote Visita';
        return zoneName; // Fallback
    };

    const renderVersusMode = () => {
        if (zones.length < 2) return null;
        const zoneA = zones[0];
        const zoneB = zones[1];
        const styleA = getZoneStyle(zoneA.zone);
        const styleB = getZoneStyle(zoneB.zone);

        return (
            <div className="noisemeter-screen__versus">
                <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--left">
                    <div className="noisemeter-screen__bar-track">
                        <div className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleA.colorClass}`} style={{ height: `${zoneA.progressPercent}%`, background: styleA.hex }}>
                            <span className="noisemeter-screen__score">{Math.floor(zoneA.progressPercent)}%</span>
                        </div>
                    </div>
                </div>
                <div className="noisemeter-screen__vs-badge">VS</div>
                <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--right">
                    <div className="noisemeter-screen__bar-track">
                        <div className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleB.colorClass}`} style={{ height: `${zoneB.progressPercent}%`, background: styleB.hex }}>
                            <span className="noisemeter-screen__score">{Math.floor(zoneB.progressPercent)}%</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderMultiMode = () => {
        return (
            <div className="noisemeter-screen__multi">
                {zones.map((z) => {
                    const style = getZoneStyle(z.zone);
                    const labelFontSize = zones.length > 5 ? '1.2rem' : '1.8rem';
                    // Reemplazamos la extracción de "Zona X" por nuestra máscara visual
                    const labelText = getVisualZoneName(z.zone); 
                    return (
                        <div key={z.zone} className="noisemeter-screen__multi-column">
                            <div className="noisemeter-screen__multi-track">
                                <div className="noisemeter-screen__multi-fill" style={{ height: `${z.progressPercent}%`, background: style.hex, boxShadow: `0 0 20px ${style.hex}60` }}></div>
                            </div>
                            <p className="noisemeter-screen__label" style={{ fontSize: labelFontSize }}>{labelText}</p>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="noisemeter-screen">
            <div className="noisemeter-screen__bg">
                {isActiveWomensMode ? (
                    <img src='/img/games/runtogoal/background-1.png' className='noisemeter-screen__bg-img' alt="Fondo Femenil" />
                ) : (
                    <img src='/img/games/runtogoal/background-1.png' className='noisemeter-screen__bg-img' alt="Fondo Varonil" />
                )}
            </div>

            <div className='Noisemeter__Screen__Box'>
                <div className="noisemeter-screen__header">
                    <img src="/img/page/xolos-five.png" className="Noisemeter__Screen__Logo" alt="Logo" />
                </div>

                <div className="noisemeter-screen__content">
                    {gameState === 'WAITING' && (
                        <p className="Noisemeter__Screen__Text--Title m-auto">Preparando dinámica...</p>
                    )}

                    {gameState === 'RUNNING' && (
                        <>
                           {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
                        </>
                    )}

                    {gameState === 'FINISHED' && (
                        <>
                            <div style={{opacity: 0.3, pointerEvents: 'none', filter: 'blur(5px)'}}>
                                {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
                            </div>

                            {winner && (
                                <div className="Results__FullScreenOverlay">
                                    <Confetti width={width} height={height} recycle={false} numberOfPieces={500} />
                                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1.12 }} transition={{ duration: 0.6, type: 'spring' }} style={{textAlign: 'center'}}>
                                        <p><i className="bi bi-trophy-fill" style={{fontSize: '5rem', color: '#ffc107'}}></i></p>
                                        
                                        {winner === 'tie' ? 
                                            <p className="Results__FullScreenOverlay__OneWinner">¡Ha habido un empate!</p> :
                                            <>
                                                <p className="Results__FullScreenOverlay__Title" style={{fontSize: '3rem'}}>El ganador es...</p>
                                                {/* Aplicamos la máscara al ganador */}
                                                <p className="Results__FullScreenOverlay__OneWinner">{getVisualZoneName(winner).toUpperCase()}</p>
                                            </>
                                        }
                                    </motion.div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <footer className='SelfieFan__Screen__QR__Footer FooterCredits'>Hecho con ♥ por Five Digital Minds | 2026</footer>
        </div>
    );
};

export { Noisemeter };













// import { useEffect, useRef, useState } from 'react'

// import { GetScreenState as BLGetScreenState } from './../../utilities/BL/Admin/Overlays/Noisemeter'

// import { motion } from 'framer-motion'
// import Confetti from 'react-confetti'


// const Noisemeter = ({ data, isActiveWomensMode }) => { 
    
//     // --- ESTADOS ---
//     const [gameState, setGameState] = useState('WAITING'); 
//     const [zones, setZones] = useState([]);
//     const [winner, setWinner] = useState(null);

//     const pollingRef = useRef(null);
//     const { innerWidth: width, innerHeight: height } = window;

//     // --------------------------------------------------------------------------------
//     // LÓGICA DE DATOS
//     // --------------------------------------------------------------------------------
//     useEffect(() => {
        
//         const updateScreen = (incoming) => {
//             if (!incoming) return;

//             // 1. NORMALIZACIÓN DE DATOS
//             const rawStatus = incoming.status !== undefined ? incoming.status : incoming.Status;
//             const rawWinner = incoming.winnerZone || incoming.WinnerZone;
            
//             // Buscamos Zones (con datos) Y activeZonesList (solo nombres)
//             const rawZones = incoming.zones || incoming.Zones;
//             const rawActiveList = incoming.activeZonesList || incoming.ActiveZonesList;
            
//             const hasDates = incoming.startAt || incoming.StartAt;

//             // 2. ACTUALIZACIÓN DE BARRAS (Lógica Mejorada)
            
//             // PRIORIDAD 1: Si vienen zonas con datos (del Polling o Finished), úsalas.
//             if (rawZones && Array.isArray(rawZones) && rawZones.length > 0) {
//                 const cleanZones = rawZones.map(z => ({
//                     zone: z.zone || z.Zone,
//                     progressPercent: z.progressPercent !== undefined ? z.progressPercent : z.ProgressPercent
//                 }));
//                 setZones(cleanZones);
//             } 
//             // PRIORIDAD 2: Si NO hay datos, pero sí hay lista de nombres (del Prepare), inicializa en 0.
//             else if (rawActiveList && Array.isArray(rawActiveList) && rawActiveList.length > 0) {
//                 // Solo inicializamos si no tenemos zonas ya cargadas, para evitar parpadeos
//                 // O si queremos forzar el reinicio visual.
//                 const initialZones = rawActiveList.map(name => ({
//                     zone: name,
//                     progressPercent: 0
//                 }));
//                 setZones(initialZones);
//             }

//             // 3. DETERMINAR ESTADO

//             // --- CASO A: YA HAY GANADOR ---
//             if (rawWinner && rawWinner !== 'none' && rawWinner !== 'tie' && rawWinner !== '') {
//                 setWinner(rawWinner);
//                 setGameState('FINISHED');
//                 return; 
//             }
//             if (rawWinner === 'tie') {
//                 setWinner('tie');
//                 setGameState('FINISHED');
//                 return;
//             }
//             if (rawStatus === 2 || rawStatus === 'Finished') {
//                 setGameState('FINISHED');
//                 return;
//             }

//             // --- CASO B: JUEGO CORRIENDO ---
//             // Criterio 1: Status explícito
//             // Criterio 2: Prepare implícito (sin status pero con fechas)
//             const isRunningExplicit = rawStatus === 1 || rawStatus === 'Running';
//             const isImplicitStart = rawStatus === undefined && hasDates; 

//             if (isRunningExplicit || isImplicitStart) {
//                 setGameState('RUNNING');
//                 setWinner(null); 
//                 return;
//             }

//             // --- CASO C: ESPERA ---
//             setGameState('WAITING');
//             setWinner(null);
//         };

//         // A. Procesar datos por props (WebSocket / Padre)
//         if (data) updateScreen(data);

//         // B. Polling (Consultar API)
//         const fetchState = async () => {
//             if (typeof BLGetScreenState !== 'undefined') {
//                 try {
//                     const res = await BLGetScreenState();
//                     if (res?.success && res?.data) {
//                         updateScreen(res.data);
//                     }
//                 } catch (e) {
//                     console.error("Error fetching screen state", e);
//                 }
//             }
//         };

//         if (!pollingRef.current) {
//             pollingRef.current = setInterval(fetchState, 500);
//         }

//         return () => {
//             if (pollingRef.current) clearInterval(pollingRef.current);
//         };

//     }, [data]); 

//     // --------------------------------------------------------------------------------
//     // RENDERIZADO VISUAL
//     // --------------------------------------------------------------------------------
//     const getZoneStyle = (zoneName) => {
//         const n = (zoneName || '').toLowerCase();
//         if (n.includes('azul')) return { colorClass: 'blue', hex: '#004d98' };
//         if (n.includes('oro')) return { colorClass: 'gold', hex: '#ffd700' };
//         if (n.includes('canels')) return { colorClass: 'blue', hex: '#0055a5' };
//         if (n.includes('tecate')) return { colorClass: 'red', hex: '#cb0909' };
//         if (n.includes('mobil')) return { colorClass: 'red', hex: '#ee1c25' };
//         if (n.includes('red cola')) return { colorClass: 'red', hex: '#da291c' };
//         if (n.includes('moctezuma')) return { colorClass: 'green', hex: '#005826' };
//         if (n.includes('nissan')) return { colorClass: 'gray', hex: '#666666' };
//         return { colorClass: 'red', hex: '#dc3545' };
//     };

//     const renderVersusMode = () => {
//         if (zones.length < 2) return null;
//         const zoneA = zones[0];
//         const zoneB = zones[1];
//         const styleA = getZoneStyle(zoneA.zone);
//         const styleB = getZoneStyle(zoneB.zone);

//         return (
//             <div className="noisemeter-screen__versus">
//                 <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--left">
//                     <div className="noisemeter-screen__bar-track">
//                         <div className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleA.colorClass}`} style={{ height: `${zoneA.progressPercent}%`, background: styleA.hex }}>
//                             <span className="noisemeter-screen__score">{Math.floor(zoneA.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>
//                 <div className="noisemeter-screen__vs-badge">VS</div>
//                 <div className="noisemeter-screen__bar-container noisemeter-screen__bar-container--right">
//                     <div className="noisemeter-screen__bar-track">
//                         <div className={`noisemeter-screen__bar-fill noisemeter-screen__bar-fill--${styleB.colorClass}`} style={{ height: `${zoneB.progressPercent}%`, background: styleB.hex }}>
//                             <span className="noisemeter-screen__score">{Math.floor(zoneB.progressPercent)}%</span>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         );
//     };

//     const renderMultiMode = () => {
//         return (
//             <div className="noisemeter-screen__multi">
//                 {zones.map((z) => {
//                     const style = getZoneStyle(z.zone);
//                     const labelFontSize = zones.length > 5 ? '1.2rem' : '1.8rem';
//                     const labelText = z.zone.replace(/Zona\s/i, ''); 
//                     return (
//                         <div key={z.zone} className="noisemeter-screen__multi-column">
//                             <div className="noisemeter-screen__multi-track">
//                                 <div className="noisemeter-screen__multi-fill" style={{ height: `${z.progressPercent}%`, background: style.hex, boxShadow: `0 0 20px ${style.hex}60` }}></div>
//                             </div>
//                             <p className="noisemeter-screen__label" style={{ fontSize: labelFontSize }}>{labelText}</p>
//                         </div>
//                     );
//                 })}
//             </div>
//         );
//     };

//     return (
//         <div className="noisemeter-screen">
//             <div className="noisemeter-screen__bg">
//                 {isActiveWomensMode ? (
//                     <img src='/img/games/runtogoal/background-1.png' className='noisemeter-screen__bg-img' alt="Fondo Femenil" />
//                 ) : (
//                     <img src='/img/games/runtogoal/background-1.png' className='noisemeter-screen__bg-img' alt="Fondo Varonil" />
//                 )}
//             </div>

//             <div className='Noisemeter__Screen__Box'>
//                 <div className="noisemeter-screen__header">
//                     <img src="/img/page/xolos-five.png" className="Noisemeter__Screen__Logo" alt="Logo" />
//                 </div>

//                 <div className="noisemeter-screen__content">
//                     {gameState === 'WAITING' && (
//                         <p className="Noisemeter__Screen__Text--Title m-auto">Preparando dinámica...</p>
//                     )}

//                     {gameState === 'RUNNING' && (
//                         <>
//                            {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
//                         </>
//                     )}

//                     {gameState === 'FINISHED' && (
//                         <>
//                             <div style={{opacity: 0.3, pointerEvents: 'none', filter: 'blur(5px)'}}>
//                                 {zones.length === 2 ? renderVersusMode() : renderMultiMode()}
//                             </div>

//                             {winner && (
//                                 <div className="Results__FullScreenOverlay">
//                                     <Confetti width={width} height={height} recycle={false} numberOfPieces={500} />
//                                     <motion.div initial={{ scale: 0 }} animate={{ scale: 1.12 }} transition={{ duration: 0.6, type: 'spring' }} style={{textAlign: 'center'}}>
//                                         <p><i className="bi bi-trophy-fill" style={{fontSize: '5rem', color: '#ffc107'}}></i></p>
                                        
//                                         {winner === 'tie' ? 
//                                             <p className="Results__FullScreenOverlay__OneWinner">¡Ha habido un empate!</p> :
//                                             <>
//                                                 <p className="Results__FullScreenOverlay__Title" style={{fontSize: '3rem'}}>El ganador es...</p>
//                                                 <p className="Results__FullScreenOverlay__OneWinner">{winner.toUpperCase()}</p>
//                                             </>
//                                         }
//                                     </motion.div>
//                                 </div>
//                             )}
//                         </>
//                     )}
//                 </div>
//             </div>
//             <footer className='SelfieFan__Screen__QR__Footer FooterCredits'>Hecho con ♥ por Five Digital Minds | 2026</footer>
//         </div>
//     );
// };

// export { Noisemeter };